<template>
  <div>
    <b-navbar id="header" type="light" variant="light" sticky>
      <b-navbar-nav class="">
        <h4 class="mb-0">Начисления зарплаты</h4>
      </b-navbar-nav>
      <b-navbar-nav class="ml-auto">
        <b-row>
          <b-col cols="auto">
            <b-dropdown v-if="canEditAccruals()" id="dropdown-create" right size="sm" text="Новое" variant="success">
              <template #button-content>
                <font-awesome-icon :icon="['fas', 'plus']" />
                Новое
              </template>
              <b-dropdown-item-button @click="onCreate(1)">Начисление Аванса</b-dropdown-item-button>
              <b-dropdown-item-button @click="onCreate(2)">Начисление Зарплаты</b-dropdown-item-button>
              <b-dropdown-item-button @click="onCreate(3)">Начисление Отпуска</b-dropdown-item-button>
              <b-dropdown-item-button @click="onCreate(4)">Начисление Премии</b-dropdown-item-button>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item-button @click="onCreate(5)">Начисление Аванса Сдельно</b-dropdown-item-button>
              <b-dropdown-item-button @click="onCreate(6)">Начисление Зарплаты Сдельно</b-dropdown-item-button>
              <b-dropdown-item-button @click="onCreate(7)">Начисление Премии Сдельно</b-dropdown-item-button>
            </b-dropdown>
          </b-col>
        </b-row>
      </b-navbar-nav>
    </b-navbar>

    <b-navbar id="filters">
      <b-navbar-nav>
        <b-form inline @submit.prevent="onSearch()">
          <b-form-input
            class="mb-1 mr-2"
            size="sm"
            v-model="filters.query"
            debounce="500"
            placeholder="Поиск" />

          <month-picker
            class="mb-1 mr-2"
            v-model="filters.period"
            label="Период"
            placeholder="Выберите период"
            :min-year="2025"
            :clearable = true
          />

          <organization-select
            id="input-organization-select"
            ref="organization_select"
            class="mb-1 mr-2"
            v-bind:initial-id="filters.org_id"
            v-on:onSelect="onSelectOrganization"
          />

          <b-button
            class="mb-1 mr-1"
            size="sm"
            variant="outline-success"
            type="submit"
          >
            Применить
          </b-button>
        </b-form>
      </b-navbar-nav>
    </b-navbar>

    <b-table
      id="accruals"
      ref="accruals"
      class="mb-0 text-small"
      selectable
      select-mode="single"
      selectedVariant="success"
      striped
      small
      responsive
      bordered
      show-empty
      :empty-text="$t('message.common.emptyTable')"
      :busy="isBusy"
      :items="items"
      :fields="fields"
      no-provider-filtering
      no-provider-paging
      no-provider-sorting
      @row-dblclicked="onRowClicked"
      sticky-header
      head-variant="light"
    >
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle"/>
          <strong>Загрузка</strong>
        </div>
      </template>

      <template #cell(active)="row">
        <font-awesome-icon v-if="!row.item.active" icon="times"/>
        <font-awesome-icon v-if="row.item.active" icon="check"/>
      </template>

      <template #cell(staffs)="row">
        <div class="text-small" v-if="row.item.staffs.length<50">{{ row.item.staffs }}</div>
        <div class="text-small" v-else>{{ row.item.staffs.substring(0, 70) + ".." }}</div>
      </template>

      <template #cell(action)="row">
        <b-btn v-if="canEditAccruals()" size="sm" variant="primary" @click="onEdit(row.item)">
          <font-awesome-icon icon="edit"/>
        </b-btn>
      </template>
    </b-table>

    <pagination-footer
      :total-rows="totalRows"
      :per-page.sync="perPage"
      @page-click="onPagination"
      @per-page-change="onPerPageChange"
    />
  </div>
</template>

<script>
import accruals from "../../network/accruals";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import permissions, {PERM} from "../../utilites/permissions";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
    import MonthPicker from "../MonthPicker/MonthPicker";
    import OrganizationSelect from "../Organizations/OrganizationSelect";

export default {
  name: "Accruals",
  components: { MonthPicker, OrganizationSelect, PaginationFooter },
  mixins: [
    accruals,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      items: [],
      fields: [
        {
          key: 'id',
          sortable: false,
          class: 'accrual-id'
        },
        {
          key: 'period',
          label: "Период",
          sortable: false,
          class: 'accrual-period',
          formatter: 'formatTableMonthPeriod'
        },
        {
          key: 'accrued_date',
          label: "Дата",
          class: 'accrual-date',
          sortable: false,
          formatter: 'formatTableDate'
        },
        {
          key: 'code',
          label: "Номер",
          sortable: false,
          class: 'accrual-code',
        },
        {
          key: 'accrual_type_name',
          label: "Тип документа",
          sortable: false,
          class: 'accrual-type',
        },
        {
          key: 'organization_name',
          label: "Организация",
          sortable: false,
          class: 'accrual-organization-name',
        },
        {
          key: 'amount_sum',
          label: "Начисленно",
          sortable: false,
          class: 'accrual-amount-sum',
          formatter: 'priceFormat',
        },
        {
          key: 'ndfl_amount_sum',
          label: "НДФЛ",
          sortable: false,
          class: 'accrual-ndfl_amount-sum',
          formatter: 'priceFormat',
        },
        {
          key: 'insurance_amount_sum',
          label: "Страховые",
          sortable: false,
          class: 'accrual-insurance-amount-sum',
          formatter: 'priceFormat',
        },
        {
          key: 'pension_before_amount_sum',
          label: "Пенс. до превыш.",
          sortable: false,
          class: 'accrual-pension-amount-sum',
          formatter: 'priceFormat',
        },
        {
          key: 'pension_after_amount_sum',
          label: "Пенс. после превыш.",
          sortable: false,
          class: 'accrual-pension-amount-sum',
          formatter: 'priceFormat',
        },
        {
          key: 'comment',
          label: "Коментарий",
          sortable: false,
          class: 'accrual-comment',
        },
        {
          key: 'staffs',
          label: "Сотрудники",
          sortable: false,
          class: 'accrual-staff',
        },
        {
          key: 'action',
          label: "Действия",
          class: 'accrual-action'
        },
      ],
      isBusy: false,
      sortBy: 'id',
      sortDesc: true,
      filters: {
        query: '',
        period: null,
        org_id: 0,
      },
      deleteRow: {
        id: null,
        name: ''
      },
    }
  },
  async created() {
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.period) {
        this.filters.period = this.dateFromServerString(query.period);
      }

      if (query.org_id > 0) {
        this.filters.org_id = query.org_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.getItems();
  },
  async mounted() {
    await this.configureRefs();
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
      }

      if (this.filters.period != null) {
        data.period = this.formatDateOnly(this.filters.period);
      }

      if (this.filters.org_id > 0) {
        data.org_id = this.filters.org_id;
      }

      this.isBusy = true;
      const result = await this.getAccruals(data);
      this.isBusy = false;

      if (result.data == null) {
        this.items = [];
        this.totalRows = 0;
        const mess = "Что-то пошло не так. Попробуйте обновить страницу и повторить действие."
        this.showError(mess);
        return;
      }

      result.data.items.forEach((item, index) => {
        result.data.items[index].staffs = this.mapStaffs(item.accrual_staffs);
      })

      this.items = result.data.items;
      this.totalRows = result.data.total;
    },
    onCreate(typeId) {
      if (typeId != null) {
        if (this.hasPermission([PERM.STAFF_EDIT])) {
          this.$router.push({name: 'AccrualAdd', params: { type_id: typeId }}).catch(()=>{});
        }
      }
    },
    onEdit(item) {
      if (this.hasPermission([PERM.STAFF_EDIT])) {
        this.$router.push({name: 'AccrualEdit', params: { id: item.id }}).catch(()=>{});
      }
    },
    async configureRefs() {
      if (this.$refs.organization_select != null) {
        await this.$refs.organization_select.configure(this.filters.org_id);
      }
    },
    onRowClicked(item) {
      this.onEdit(item);
    },
    formatTableMonthPeriod(date) {
      const options = {
        year: "numeric",
        month: "long",
      };
      return this.capitalizeFirstLetter(
        new Date(this.formattedFromServerDate(date))
          .toLocaleDateString("ru-RU", options));
    },
    formattedFromServerDate(date) {
      if (date != null) {
        date = this.formatDate(this.getDateFromServerString(date));
      }
      return date;
    },
    capitalizeFirstLetter(val) {
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    },
    // Salaries Totals
    calculateTotal(fieldKey, item) {
      if (item.salaries == null) {
        return null;
      }

      if (fieldKey === 'length') {
        return item.salaries.length;
      }

      const result = item.salaries
        .map(obj => obj[fieldKey])
        .reduce((acc, current) => acc + current, 0);

      return Math.round(result * 100) / 100;
    },
    price(value) {
      if (value == null) { return value; }
      if (!this.isCustomNumber(value)) { return value; }
      return this.priceFormat(value, false);
    },
    mapStaffs(staffs) {
      if (staffs == null) { return ''}
      return staffs.map(obj => obj.staff_name).join(', ');
    },
    onSelectOrganization(id) {
      this.filters.org_id = id;
    },
    // Permissions
    canEditAccruals() {
      return this.hasPermission([PERM.STAFF_EDIT]);
    }
  }
}
</script>

<style>
#accruals th {
  vertical-align: middle;
}

#accruals td {
  vertical-align: middle;
}

.accrual-id {
  padding-left: 10px !important;
  padding-right: 10px !important;
  width: 60px;
  min-width: 60px;
  text-align: center;
}

.accrual-period {
  min-width: 120px;
  max-width: 120px;
  width: 120px;
}

.accrual-date {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

.accrual-code {
  min-width: 120px;
  max-width: 120px;
  width: 120px;
}

.accrual-type {
  min-width: 120px;
  max-width: 120px;
  width: 120px;
}

.accrual-organization {
  min-width: 150px;
  max-width: 150px;
  width: 150px;
}

.accrual-amount-sum {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

.accrual-ndfl-amount-sum {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

.accrual-insurance-amount-sum {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

.accrual-pension-amount-sum {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
}

.accrual-comment {
  min-width: 150px;
  max-width: 150px;
  width: 150px;
}

.accrual-staff {
  min-width: 150px;
  max-width: 150px;
  width: 150px;
}

.accrual-action {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Accruals/Accruals.vue